import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { API_URL } from '../../config'; // ✅ AGREGAR ESTA IMPORTACIÓN

// Interfaces existentes
interface ExcelData {
  [key: string]: any;
}

interface ProcessedData {
  categories: Array<{
    name: string;
    description: string;
  }>;
  products: Array<{
    name: string;
    description: string;
    price: number;
    category: string;
    stock: number;
    sku: string;
    family?: string;
    subfamily?: string;
    image_url?: string;
  }>;
}

// Nueva interface para datos de familias
interface ProcessedFamilyData {
  families: Array<{
    name: string;
    categoryName: string;
  }>;
  subfamilies: Array<{
    name: string;
    familyName: string;
    categoryName: string;
  }>;
  products: Array<{
    productName: string;
    categoryName: string;
    familyName: string;
    subfamilyName: string;
  }>;
}

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean; isDragging: boolean }>`
  border: 2px dashed ${props => 
    props.isDragActive ? '#4CAF50' : 
    props.isDragging ? '#2196F3' : '#ddd'
  };
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${props => 
    props.isDragActive ? '#f8fff8' : 
    props.isDragging ? '#f0f8ff' : '#fafafa'
  };
  margin-bottom: 20px;

  &:hover {
    border-color: #2196F3;
    background-color: #f0f8ff;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileInfo = styled.div`
  margin: 20px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 5px;
`;

const PreviewSection = styled.div`
  margin-top: 20px;
`;

const ImportButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;

  &:hover {
    background: #45a049;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  margin: 10px 0;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.progress}%;
    background-color: #4CAF50;
    transition: width 0.3s ease;
  }
`;

const UpdateButton = styled.button`
  background: #FDBF37;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
  margin-left: 10px;

  &:hover {
    background: #FDBF37;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const ModeButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  border: 2px solid ${props => props.active ? '#2196F3' : '#ddd'};
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 5px;
  cursor: pointer;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  transition: all 0.3s ease;

  &:hover {
    border-color: #2196F3;
    background: ${props => props.active ? '#1976D2' : '#f0f8ff'};
  }
`;

// SVG Icons
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,5 17,10" />
    <line x1="12" y1="5" x2="12" y2="15" />
  </svg>
);

const FileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

const ExcelUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  const [processedFamilyData, setProcessedFamilyData] = useState<ProcessedFamilyData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [processingMode, setProcessingMode] = useState<'products' | 'families'>('products');
  const [operationMode, setOperationMode] = useState<'import' | 'export'>('import');
  const [running, setRunning] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [syncSummary, setSyncSummary] = useState<any | null>(null);

  // Manejo de eventos de drag & drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const excelFile = droppedFiles.find(file => 
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );

    if (excelFile) {
      setFile(excelFile);
      if (operationMode === 'import') {
        processExcelFile(excelFile);
      }
    } else {
      toast.error('Por favor, selecciona un archivo Excel válido (.xlsx o .xls)');
    }
  }, [operationMode]);

  // Manejo de selección de archivo por click
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (operationMode === 'import') {
        processExcelFile(selectedFile);
      }
    }
  }, [operationMode]);

  // Función para abrir el selector de archivos
  const openFileDialog = useCallback(() => {
    const input = document.getElementById('file-input') as HTMLInputElement;
    input?.click();
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setRunning(true);
      const controller = new AbortController();
      setAbortController(controller);
      const response = await fetch(`${API_URL}/excel/export/products`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        signal: controller.signal
      });
      if (!response.ok) {
        toast.error('Error al exportar');
        setRunning(false);
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      let filename = `productos pagina web vargashomecenter - ${yyyy}-${mm}-${dd}.xlsx`;
      const match = disposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportación completada');
    } catch (e) {
      toast.error('Exportación cancelada o fallida');
    } finally {
      setRunning(false);
      setAbortController(null);
    }
  }, []);

  const cancelRunning = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
    setRunning(false);
  }, [abortController]);

  // ✅ Mejorar la función de procesamiento
  // ✅ Función mejorada para manejar archivos con columnas __EMPTY
  const processExcelFile = async (file: File) => {
    setIsProcessing(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // ✅ SOLUCIÓN: Leer con header en fila específica y rango completo
      const jsonData: ExcelData[] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1, // Leer como array de arrays primero
        defval: '', // Valor por defecto para celdas vacías
        raw: false // Convertir todo a string
      });
  
      console.log('Datos crudos del Excel:', jsonData.slice(0, 5)); // Mostrar primeras 5 filas
      
      let headerRowIndex = 0;
      let headers: string[] = [];
      const candidates = jsonData.slice(0, Math.min(10, jsonData.length)) as string[][];
      const synonyms = [
        'id','sku','name','nombre','description','descripcion','price','precio','stock','featured','destacado','brand','marca','model','modelo','category_name','categoria','categoría','family_name','familia','subfamily_name','subfamilia'
      ];
      let bestIndex = -1;
      let bestScore = -1;
      for (let i = 0; i < candidates.length; i++) {
        const row = candidates[i].map(c => String(c || '').trim().toLowerCase());
        let score = 0;
        for (const cell of row) {
          const norm = cell.normalize('NFD').replace(/[^a-z_]/g, '');
          if (synonyms.includes(norm)) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          bestIndex = i;
        }
      }
      headerRowIndex = bestIndex >= 0 ? bestIndex : 0;
      headers = (jsonData[headerRowIndex] as string[]).map(cell => String(cell || '').trim());
      
      // ✅ Convertir datos usando los encabezados encontrados
      const dataRows = jsonData.slice(headerRowIndex + 1) as string[][];
      const processedJsonData = dataRows.map(row => {
        const obj: ExcelData = {};
        headers.forEach((header, index) => {
          if (header) {
            obj[header] = row[index] || '';
          }
        });
        return obj;
      });
      
      console.log('Datos procesados:', processedJsonData.slice(0, 3));
        console.log('Encabezados finales:', headers);
  
      // ✅ Buscar categorías con los encabezados reales
      const categoryNames = processedJsonData
        .map(row => {
          // Buscar en todos los encabezados que contengan "categoria" (con o sin acento)
          const categoryHeader = headers.find(h => {
            const headerLower = h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            return headerLower.includes('categoria') || 
                   headerLower.includes('category') ||
                   h.toLowerCase().includes('categoría') ||
                   h.includes('Categoría Final');
          });
          
          if (!categoryHeader) {
            console.log('No se encontró encabezado de categoría para la fila:', row);
            return null;
          }
          
          const category = row[categoryHeader];
          const categoryStr = category ? String(category).trim() : null;
          console.log(`Categoría encontrada: "${categoryStr}" del encabezado: "${categoryHeader}"`)
          
          // Filtrar valores no válidos
          if (!categoryStr || 
              categoryStr === 'undefined' || 
              categoryStr === 'null' || 
              categoryStr === '' ||
              categoryStr.toLowerCase() === 'sin categoria' ||
              categoryStr.toLowerCase() === 'sin categoría') {
            return null;
          }
          
          return categoryStr;
        })
        .filter((name): name is string => Boolean(name));
  
      const uniqueCategories = Array.from(new Set(categoryNames));
      
      console.log('Nombres de categorías encontrados:', categoryNames.slice(0, 10));
      console.log('Categorías únicas:', uniqueCategories);
      
      if (uniqueCategories.length === 0) {
        console.warn('Encabezados disponibles:', headers);
        toast.error(`No se encontraron categorías válidas. Encabezados disponibles: ${headers.join(', ')}`);
        setIsProcessing(false);
        return;
      }
      
      const categories = uniqueCategories.map(name => ({
        name: String(name),
        description: `Categoría ${name}`
      }));
  
      console.log('Categorías procesadas:', categories);
  
      // ✅ Procesar productos con encabezados dinámicos
      const products = processedJsonData.map((row, index) => {
        console.log(`Procesando fila ${index}:`, row);
        
        if (!row || typeof row !== 'object') {
          console.log(`Fila ${index} no es válida:`, row);
          return null;
        }
        
        // Buscar encabezados dinámicamente con mejor detección
        const categoryHeader = headers.find(h => {
          const headerLower = h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return headerLower.includes('categoria') || 
                 headerLower.includes('category') ||
                 h.toLowerCase().includes('categoría') ||
                 h.includes('Categoría Final');
        });
        
        // Detectar código de item separado del nombre
        const itemCodeHeader = headers.find(h => 
          h.toLowerCase().includes('codigo') ||
          h.toLowerCase().includes('código') ||
          h.toLowerCase().includes('item') ||
          h === 'Item' ||
          h.toLowerCase().includes('id') ||
          h.toLowerCase().includes('numero') ||
          h.toLowerCase().includes('número')
        );
        
        const nameHeader = headers.find(h => 
          h.toLowerCase().includes('nombre') || 
          h.toLowerCase().includes('name') ||
          h.toLowerCase().includes('descripcion') ||
          h.toLowerCase().includes('description') ||
          h.toLowerCase().includes('producto')
        );
        
        const priceHeader = headers.find(h => 
          h.toLowerCase().includes('precio') || 
          h.toLowerCase().includes('price') ||
          h.includes('Precio Unitario')
        );
        const stockHeader = headers.find(h => 
          h.toLowerCase().includes('stock') || 
          h.toLowerCase().includes('cantidad')
        );
        const skuHeader = headers.find(h => 
          h.toLowerCase().includes('sku') || 
          h.toLowerCase().includes('codigo') ||
          h === 'Item'
        );
        const familyHeader = headers.find(h => {
          const l = h.toLowerCase();
          const n = l.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return n.includes('familia') || l.includes('family_name') || l.includes('family');
        });
        const subfamilyHeader = headers.find(h => {
          const l = h.toLowerCase();
          const n = l.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return n.includes('subfamilia') || l.includes('subfamily_name') || l.includes('subfamily');
        });
        const imageHeader = headers.find(h => {
          const l = h.toLowerCase();
          const n = l.normalize('NFD').replace(/[^a-z_]/g, '');
          return n.includes('image_url') || n.includes('imagen') || l.includes('image');
        });
        
        console.log(`Headers encontrados para productos:`, {
          categoryHeader,
          itemCodeHeader,
          nameHeader,
          priceHeader,
          stockHeader,
          skuHeader
        });
        
        // Función para parsear precios en formato peruano
        const parsePrice = (priceStr: string): number => {
          if (!priceStr) return 0;
          // Remover "S/", espacios y convertir comas a puntos
          const cleanPrice = String(priceStr)
            .replace(/S\/\s*/g, '')
            .replace(/,/g, '.')
            .trim();
          const parsed = parseFloat(cleanPrice);
          return isNaN(parsed) ? 0 : parsed;
        };

        // Obtener código del item y nombre por separado
        let itemCode = String(row[itemCodeHeader || ''] || '').trim();
        let itemName = String(row[nameHeader || ''] || '').trim();
        
        // Si el nombre contiene información extra (precios, monedas, etc.), limpiarla
        if (itemName) {
          // Remover información de precios y monedas del nombre
          itemName = itemName
            .replace(/USD\s+[\d,]+\.?\d*/gi, '') // Remover USD y números
            .replace(/\$\s*[\d,]+\.?\d*/g, '') // Remover $ y números
            .replace(/OPE:\d+/gi, '') // Remover OPE:números
            .replace(/T\.C\s*[\d.]+/gi, '') // Remover T.C y números
            .replace(/S\/\s*[\d,]+\.?\d*/g, '') // Remover S/ y números
            .replace(/\s+/g, ' ') // Normalizar espacios
            .trim();
        }
        
        // Si no hay código separado, intentar extraerlo del inicio del nombre
        if (!itemCode && itemName) {
          const match = itemName.match(/^(\d+)\s*-?\s*(.+)$/);
          if (match) {
            itemCode = match[1].trim();
            itemName = match[2].trim();
          }
        }
        
        // Formatear el código de item a 5 dígitos con ceros a la izquierda
        if (itemCode && /^\d+$/.test(itemCode)) {
          itemCode = itemCode.padStart(5, '0');
          console.log(`Código formateado a 5 dígitos: ${itemCode}`);
        }
        
        // Limpiar el nombre de cualquier información residual de precios
        if (itemName) {
          // Buscar donde termina el nombre real (antes de información de precios)
          const priceIndicators = /\b(USD|S\/|\$|OPE:|T\.C)/i;
          const priceMatch = itemName.search(priceIndicators);
          if (priceMatch > 0) {
            itemName = itemName.substring(0, priceMatch).trim();
          }
        }
        
        // Concatenar código + nombre como en la base de datos (formato: "CÓDIGO - NOMBRE")
        let fullProductName = '';
        if (itemCode && itemName) {
          fullProductName = `${itemCode} - ${itemName}`;
        } else if (itemCode) {
          fullProductName = itemCode;
        } else if (itemName) {
          fullProductName = itemName;
        } else {
          fullProductName = `Producto ${index + 1}`;
        }
        
        console.log(`Producto ${index}: código="${itemCode}", nombre="${itemName}", final="${fullProductName}"`);

        return {
          name: fullProductName,
          description: String(row['descripcion'] || row['description'] || ''),
          price: parsePrice(row[priceHeader || ''] || '0'),
          category: String(row[categoryHeader || ''] || 'Sin categoría').trim(),
          stock: Number(row[stockHeader || ''] || 0),
          sku: String(row[skuHeader || ''] || itemCode || `SKU-${Date.now()}-${index}`),
          family: String(row[familyHeader || ''] || '').trim(),
          subfamily: String(row[subfamilyHeader || ''] || '').trim(),
          image_url: String(row[imageHeader || ''] || '').trim()
        };
      }).filter((product): product is NonNullable<typeof product> => product !== null);
  
      // Normalizar códigos de item a formato de 5 dígitos (sin eliminar duplicados)
      const normalizedProducts = [];
      
      for (const product of products.filter((p): p is NonNullable<typeof p> => p !== null)) {
        // Extraer el código del nombre del producto
        const nameMatch = product.name.match(/^(\d+)\s*-\s*(.+)$/);
        if (nameMatch) {
          const code = nameMatch[1];
          const baseName = nameMatch[2];
          
          // Normalizar el código a 5 dígitos
          const normalizedCode = code.padStart(5, '0');
          
          // Actualizar el nombre del producto con el código normalizado
          product.name = `${normalizedCode} - ${baseName}`;
          product.sku = normalizedCode; // También actualizar el SKU
          
          console.log(`Código normalizado: ${code} → ${normalizedCode} para producto: ${baseName}`);
        }
        
        // Agregar todos los productos (sin eliminar duplicados)
        normalizedProducts.push(product);
      }
  
      console.log(`Productos procesados: ${products.length}, Códigos normalizados: ${normalizedProducts.length}`);
  
      setProcessedData({ categories, products: normalizedProducts as ProcessedData['products'] });
      toast.success(`Archivo procesado: ${categories.length} categorías y ${normalizedProducts.length} productos con códigos normalizados`);
    } catch (error) {
      console.error('Error procesando archivo:', error);
      toast.error('Error al procesar el archivo Excel');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // ✅ Función para procesar en lotes
  const handleImport = async () => {
    if (!processedData) return;

    setIsImporting(true);
    setImportProgress(0);
  
    try {
      console.log('Iniciando importación...');
      
      // ✅ VERIFICAR QUE HAY CATEGORÍAS
      if (processedData.categories.length === 0) {
        throw new Error('No hay categorías para importar');
      }
      
      // Importar categorías primero
      console.log('Importando categorías:', processedData.categories);
      const categoryResponse = await fetch(`${API_URL}/excel/import/categories`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ categories: processedData.categories })
      });
  
      if (!categoryResponse.ok) {
        const errorData = await categoryResponse.text();
        console.error('Error response categorías:', errorData);
        throw new Error(`Error importando categorías: ${categoryResponse.status}`);
      }
  
      const categoryResult = await categoryResponse.json();
      console.log('Resultado categorías:', categoryResult);
      setImportProgress(20);
  
      // ✅ PROCESAR PRODUCTOS EN LOTES DE 100
      const BATCH_SIZE = 100;
      const totalProducts = processedData.products.length;
      let processedCount = 0;
      let createdCount = 0;
  
      for (let i = 0; i < totalProducts; i += BATCH_SIZE) {
        const batch = processedData.products.slice(i, i + BATCH_SIZE);
        
        console.log(`Procesando lote ${Math.floor(i/BATCH_SIZE) + 1}: productos ${i + 1} a ${Math.min(i + BATCH_SIZE, totalProducts)}`);
        
        const productResponse = await fetch(`${API_URL}/excel/import/products`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ products: batch })
        });
  
        if (!productResponse.ok) {
          const errorData = await productResponse.text();
          console.error('Error response productos:', errorData);
          throw new Error(`Error importando productos lote ${Math.floor(i/BATCH_SIZE) + 1}: ${productResponse.status}`);
        }
  
        const productResult = await productResponse.json();
        createdCount += productResult.created;
        processedCount += batch.length;
        
        // Actualizar progreso (20% categorías + 80% productos)
        const progress = 20 + (processedCount / totalProducts) * 80;
        setImportProgress(progress);
        
        // Pausa entre lotes para no sobrecargar el servidor
        if (i + BATCH_SIZE < totalProducts) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
  
      toast.success(`Importación completada: ${categoryResult.created} categorías y ${createdCount} productos creados`);
      
      // Limpiar estado
      setTimeout(() => {
        setFile(null);
        setProcessedData(null);
        setImportProgress(0);
      }, 2000);
      
    } catch (error) {
      console.error('Error en importación:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido durante la importación';
      toast.error(`Error durante la importación: ${errorMessage}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Nueva función para procesar archivos con familias y subfamilias
  const processExcelFileForFamilies = async (file: File) => {
    setIsProcessing(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      const jsonData: ExcelData[] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: false
      });

      console.log('Datos crudos del Excel para familias:', jsonData.slice(0, 5));
      console.log('Total de filas en el Excel:', jsonData.length);
      
      // Encontrar la fila de encabezados
      let headerRowIndex = -1;
      let headers: string[] = [];
      
      console.log('Buscando encabezados en las primeras 10 filas...');
      for (let i = 0; i < Math.min(10, jsonData.length); i++) {
        const row = jsonData[i] as string[];
        const rowString = row.join('|').toLowerCase();
        
        console.log(`Fila ${i}:`, row);
        console.log(`Fila ${i} (texto):`, rowString);
        
        // Buscar patrones de encabezados
        const hasProductName = rowString.includes('item') || 
                              rowString.includes('nombre') || 
                              rowString.includes('producto') ||
                              rowString.includes('descripcion') ||
                              rowString.includes('descripción');
                              
        const hasCategory = rowString.includes('categoria') || 
                           rowString.includes('categoría') ||
                           rowString.includes('category');
                           
        const hasFamily = rowString.includes('familia');
        const hasSubfamily = rowString.includes('subfamilia');
        
        console.log(`Fila ${i} - Patrones encontrados:`, {
          hasProductName,
          hasCategory,
          hasFamily,
          hasSubfamily
        });
        
        if ((hasProductName && hasCategory) || (hasProductName && hasFamily) || (hasProductName && hasSubfamily)) {
          headerRowIndex = i;
          headers = row.map(cell => String(cell || '').trim());
          console.log(`✅ Encabezados encontrados en fila ${i}:`, headers);
          break;
        }
      }
      
      // Si no se encontraron encabezados específicos, usar la primera fila no vacía
      if (headerRowIndex === -1) {
        console.log('No se encontraron patrones específicos, buscando primera fila no vacía...');
        for (let i = 0; i < Math.min(5, jsonData.length); i++) {
          const row = jsonData[i] as string[];
          const nonEmptyColumns = row.filter(cell => String(cell || '').trim().length > 0).length;
          
          if (nonEmptyColumns >= 2) {
            headerRowIndex = i;
            headers = row.map(cell => String(cell || '').trim());
            console.log(`📋 Usando fila ${i} como encabezados (respaldo):`, headers);
            break;
          }
        }
      }

      if (headerRowIndex === -1) {
        toast.error('No se encontraron encabezados válidos. Asegúrese de que el archivo tenga al menos 2 columnas con datos');
        setIsProcessing(false);
        return;
      }
      
      const dataRows = jsonData.slice(headerRowIndex + 1) as string[][];
      const processedJsonData = dataRows.map(row => {
        const obj: ExcelData = {};
        headers.forEach((header, index) => {
          if (header) {
            obj[header] = row[index] || '';
          }
        });
        return obj;
      });
      
      console.log('Datos procesados para familias:', processedJsonData.slice(0, 3));
        console.log('Datos raw para familias:', dataRows.slice(0, 3));
      
      // Buscar directamente por nombres de columnas
      console.log('Encabezados disponibles:', headers);
      
      // Mapear encabezados a índices para acceso directo
      const headerMap: { [key: string]: number } = {};
      headers.forEach((header, index) => {
        if (header && header.trim()) {
          headerMap[header.trim()] = index;
        }
      });
      
      console.log('Mapa de encabezados:', headerMap);
      
      // Buscar las columnas específicas
      console.log('Buscando columnas específicas...');
      
      const productNameHeader = headers.findIndex(header => {
        const h = header.toLowerCase();
        return h.includes('item') || h.includes('nombre') || h.includes('producto') || h.includes('descripcion');
      });
      
      const categoryHeader = headers.findIndex(header => {
        const h = header.toLowerCase();
        return h.includes('categoria') || h.includes('category');
      });
      
      const familyHeader = headers.findIndex(header => {
        const h = header.toLowerCase();
        return h.includes('familia') && !h.includes('subfamilia');
      });
      
      const subfamilyHeader = headers.findIndex(header => {
        const h = header.toLowerCase();
        return h.includes('subfamilia');
      });
      
      console.log('Índices de columnas encontrados:', {
        productNameHeader,
        categoryHeader,
        familyHeader,
        subfamilyHeader
      });

      console.log('Encabezados detectados:', {
        productNameHeader,
        categoryHeader,
        familyHeader,
        subfamilyHeader
      });

      if (productNameHeader === -1) {
        toast.error(`No se encontró columna de nombre de producto. Columnas disponibles: ${headers.join(', ')}`);
        setIsProcessing(false);
        return;
      }

      if (categoryHeader === -1 && familyHeader === -1 && subfamilyHeader === -1) {
        toast.error(`No se encontraron columnas de categoría, familia o subfamilia. Columnas disponibles: ${headers.join(', ')}`);
        setIsProcessing(false);
        return;
      }

      // Extraer familias únicas
      const familiesSet = new Set<string>();
      const subfamiliesSet = new Set<string>();
      const productsData: Array<{
        productName: string;
        categoryName: string;
        familyName: string;
        subfamilyName: string;
      }> = [];

      dataRows.forEach((row: string[], index: number) => {
          // Acceder directamente usando los índices de las columnas
          const productName = productNameHeader !== -1 ? String(row[productNameHeader] || '').trim() : '';
          const categoryName = categoryHeader !== -1 ? String(row[categoryHeader] || '').trim() : '';
          const familyName = familyHeader !== -1 ? String(row[familyHeader] || '').trim() : '';
          const subfamilyName = subfamilyHeader !== -1 ? String(row[subfamilyHeader] || '').trim() : '';

        // Solo requerimos el nombre del producto
        if (productName) {
          // Si hay familia, la agregamos
          if (familyName && categoryName) {
            familiesSet.add(`${familyName}|${categoryName}`);
          }
          
          // Si hay subfamilia y familia, la agregamos
          if (subfamilyName && familyName && categoryName) {
            subfamiliesSet.add(`${subfamilyName}|${familyName}|${categoryName}`);
          }

          // Agregamos el producto con los datos disponibles
          productsData.push({
            productName,
            categoryName,
            familyName,
            subfamilyName
          });
        }
      });

      const families = Array.from(familiesSet).map(item => {
        const [name, categoryName] = item.split('|');
        return { name, categoryName };
      });

      const subfamilies = Array.from(subfamiliesSet).map(item => {
        const [name, familyName, categoryName] = item.split('|');
        return { name, familyName, categoryName };
      });

      setProcessedFamilyData({
        families,
        subfamilies,
        products: productsData
      });

      toast.success(`Archivo procesado: ${families.length} familias, ${subfamilies.length} subfamilias y ${productsData.length} productos para actualizar`);
    } catch (error) {
      console.error('Error procesando archivo para familias:', error);
      toast.error('Error al procesar el archivo Excel para familias');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSyncImport = async () => {
    if (!processedData) return;
    setIsImporting(true);
    setImportProgress(0);
    setSyncSummary(null);
    try {
      const chunkSize = 200;
      const rows = processedData.products.map(p => ({
        name: p.name,
        description: p.description,
        price: p.price,
        stock: p.stock,
        sku: p.sku,
        category_name: p.category,
        family_name: (p as any).family || null,
        subfamily_name: (p as any).subfamily || null,
        image_url: (p as any).image_url || null
      }));
      const total = rows.length;
      const aggregate = { created: 0, updated: 0, skipped: 0, errors: 0 };
      for (let i = 0; i < total; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const response = await fetch(`${API_URL}/excel/sync/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ rows: chunk })
        });
        if (!response.ok) throw new Error('Error sincronizando productos');
        const data = await response.json();
        aggregate.created += data.summary.created;
        aggregate.updated += data.summary.updated;
        aggregate.skipped += data.summary.skipped;
        aggregate.errors += data.summary.errors;
        setImportProgress(Math.min(100, Math.round(((i + chunk.length) / total) * 100)));
      }
      setSyncSummary(aggregate);
      toast.success('Sincronización completada');
    } catch (e) {
      toast.error('Error en sincronización');
    } finally {
      setIsImporting(false);
    }
  };

  // Función para manejar la actualización con familias
  const handleUpdateWithFamilies = async () => {
    if (!processedFamilyData) return;

    setIsImporting(true);
    setImportProgress(0);

    try {
      console.log('Iniciando actualización con familias...');
      
      // Importar familias
      if (processedFamilyData.families.length > 0) {
        console.log('Importando familias:', processedFamilyData.families);
        const familyResponse = await fetch(`${API_URL}/excel/import/families`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ families: processedFamilyData.families })
        });

        if (!familyResponse.ok) {
          throw new Error(`Error importando familias: ${familyResponse.status}`);
        }

        const familyResult = await familyResponse.json();
        console.log('Resultado familias:', familyResult);
        setImportProgress(25);
      }

      // Importar subfamilias
      if (processedFamilyData.subfamilies.length > 0) {
        console.log('Importando subfamilias:', processedFamilyData.subfamilies);
        const subfamilyResponse = await fetch(`${API_URL}/excel/import/subfamilies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subfamilies: processedFamilyData.subfamilies })
        });

        if (!subfamilyResponse.ok) {
          throw new Error(`Error importando subfamilias: ${subfamilyResponse.status}`);
        }

        const subfamilyResult = await subfamilyResponse.json();
        console.log('Resultado subfamilias:', subfamilyResult);
        setImportProgress(50);
      }

      // Actualizar productos
      if (processedFamilyData.products.length > 0) {
        console.log('Actualizando productos:', processedFamilyData.products);
        const updateResponse = await fetch(`${API_URL}/excel/update/products-families`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ products: processedFamilyData.products })
        });

        if (!updateResponse.ok) {
          throw new Error(`Error actualizando productos: ${updateResponse.status}`);
        }

        const updateResult = await updateResponse.json();
        console.log('Resultado actualización:', updateResult);
        setImportProgress(100);

        // Mostrar resumen detallado
        const summary = updateResult.summary;
        let message = `✅ Proceso completado!\n`;
        message += `📊 Resumen:\n`;
        message += `• Total procesados: ${summary.total}\n`;
        message += `• Actualizados: ${summary.updated}\n`;
        
        if (summary.notFound > 0) {
          message += `• Productos no encontrados: ${summary.notFound}\n`;
        }
        if (summary.familyNotFound > 0) {
          message += `• Familias no encontradas: ${summary.familyNotFound}\n`;
        }
        if (summary.subfamilyNotFound > 0) {
          message += `• Subfamilias no encontradas: ${summary.subfamilyNotFound}\n`;
        }
        if (summary.errors > 0) {
          message += `• Errores: ${summary.errors}\n`;
        }

        // Mostrar detalles de productos no encontrados si los hay
        if (updateResult.details.notFoundProducts.length > 0) {
          console.log('Productos no encontrados:', updateResult.details.notFoundProducts);
          message += `\n⚠️ Algunos productos no se encontraron en la base de datos.`;
        }

        // Mostrar detalles de familias no encontradas si las hay
        if (updateResult.details.familyNotFoundDetails.length > 0) {
          console.log('Familias no encontradas:', updateResult.details.familyNotFoundDetails);
          message += `\n⚠️ Algunas familias no se encontraron.`;
        }

        // Mostrar detalles de subfamilias no encontradas si las hay
        if (updateResult.details.subfamilyNotFoundDetails.length > 0) {
          console.log('Subfamilias no encontradas:', updateResult.details.subfamilyNotFoundDetails);
          message += `\n⚠️ Algunas subfamilias no se encontraron.`;
        }

        if (summary.updated > 0) {
          toast.success(message);
        } else {
          toast.warning(message);
        }
      }

      // Limpiar estado
      setTimeout(() => {
        setFile(null);
        setProcessedFamilyData(null);
        setImportProgress(0);
      }, 2000);
      
    } catch (error) {
      console.error('Error en actualización:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido durante la actualización';
      toast.error(`Error durante la actualización: ${errorMessage}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Función para cambiar el modo de procesamiento
  const handleModeChange = (mode: 'products' | 'families') => {
    setProcessingMode(mode);
    // Limpiar datos cuando se cambia de modo
    setFile(null);
    setProcessedData(null);
    setProcessedFamilyData(null);
    setImportProgress(0);
  };

  return (
    <Container>
      <h2>Importar/Exportar Excel</h2>
      
      <ModeSelector>
        <h3>Selecciona la operación:</h3>
        <ModeButton
          active={operationMode === 'import'}
          onClick={() => setOperationMode('import')}
        >
          Importar
        </ModeButton>
        <ModeButton
          active={operationMode === 'export'}
          onClick={() => setOperationMode('export')}
        >
          Exportar
        </ModeButton>
      </ModeSelector>
      {operationMode === 'import' && (
        <div style={{ padding: '10px', background: '#e3f2fd', borderRadius: '5px', marginBottom: '20px' }}>
          <strong>Sincronización integral</strong>
          <p>Importa y actualiza productos, categorías, familias y subfamilias en un solo paso.</p>
        </div>
      )}
      
      {operationMode === 'import' && (
      <DropzoneContainer
        isDragActive={isDragActive}
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <HiddenInput
          id="file-input"
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileSelect}
        />
        
        <UploadIcon />
        <h3>
          {isDragActive
            ? 'Suelta el archivo aquí'
            : isDragging
            ? 'Arrastra el archivo aquí'
            : 'Arrastra y suelta tu archivo Excel aquí'}
        </h3>
        <p>o haz clic para seleccionar un archivo</p>
        <small>Formatos soportados: .xlsx, .xls</small>
      </DropzoneContainer>
      )}

      {operationMode === 'import' && isProcessing && (
        <FileInfo>
          <p>Procesando archivo...</p>
        </FileInfo>
      )}

      {operationMode === 'import' && file && !isProcessing && (
        <FileInfo>
          <FileIcon />
          <strong>Archivo seleccionado:</strong> {file.name}
          <br />
          <strong>Tamaño:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
        </FileInfo>
      )}

      {operationMode === 'import' && processedData && (
        <PreviewSection>
          <h3>Vista previa de datos - Productos</h3>
          <p><strong>Categorías encontradas:</strong> {processedData.categories.length}</p>
          <p><strong>Productos encontrados:</strong> {processedData.products.length}</p>
          
          {isImporting && (
            <div>
              <p>Importando datos... {Math.round(importProgress)}%</p>
              <ProgressBar progress={importProgress} />
            </div>
          )}
          
          <ImportButton
            onClick={handleSyncImport}
            disabled={isImporting}
          >
            {isImporting ? 'Sincronizando...' : 'Sincronizar Productos'}
          </ImportButton>
        </PreviewSection>
      )}

      {operationMode === 'export' && (
        <PreviewSection>
          {running && (
            <div>
              <p>Exportando...</p>
              <ProgressBar progress={50} />
            </div>
          )}
          <ImportButton onClick={handleExport} disabled={running}>
            {running ? 'Exportando...' : 'Exportar Productos'}
          </ImportButton>
          {running && (
            <ImportButton onClick={cancelRunning}>
              Cancelar
            </ImportButton>
          )}
        </PreviewSection>
      )}
    </Container>
  );
};

export default ExcelUpload;